<template>
  <section :class="contentClasses">
    <div class="row">
      <div class="col-12">
        <search-scope-select-bar v-if="useCombinedSearch && !isDatasetseries" :config="searchFiltersConfig" />
      </div>
    </div>
    <search-filters :config="searchFiltersConfig" v-if="!isDatasetseries" :showSaveSearch="showSaveSearch"/>
    <datasets-info-box />
    <selectedFacetsOverview
      v-if="getFacets"
      :selected-facets="getFacets"
      :available-facets="normalizedAvailableFacets"
    >
      <template #default="{ index, facet, facetId, findFacetTitle, removeSelectedFacet }">
        <span
          :key="index"
          class="badge badge-pill badge-highlight mr-1 ds-label"
        >
          {{ resolveSelectedFacetLabel(facet, facetId, findFacetTitle) }}
          <span
            @click="removeSelectedFacet(facet.field, facetId)"
            class="close-facet ml-2"
          >
            ×
          </span>
        </span>
      </template>
    </selectedFacetsOverview>
    <dataset-details-skeleton v-if="getLoading" type="Datasets" />
    <template v-if="!getLoading">
    
      <dataset-list-infos />
      <combined-list :is-datasetseries="isDatasetseries"/>
    </template>
  </section>
</template>

<script>
import {
  DatasetDetailsSkeleton,
  SelectedFacetsOverview,
} from "@piveau/piveau-hub-ui-modules";
import SearchFilters from "@/components/search/search-filters/SearchFilters";
import DatasetListInfos from "@/components/search/datasets/DatasetListInfos";
import DatasetsInfoBox from "@/components/search/datasets/DatasetsInfoBox";
import { mapGetters } from "vuex";
import SearchScopeSelectBar from "@/components/search/search-filters/SearchScopeSelectBar";
import CombinedList from "@/components/search/combined/CombinedList";

export default {
  name: "CombinedContent",
  components: {
    CombinedList,
    SearchScopeSelectBar,
    SearchFilters,
    DatasetDetailsSkeleton,
    SelectedFacetsOverview,
    DatasetListInfos,
    DatasetsInfoBox,
  },
  props: {
    isDatasetseries: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      useCombinedSearch: this.$env.content.useCombinedSearch
    }
  },
  computed: {
    searchFiltersConfig() {
      if (this.isDatasetseries) {
        return {
          scope: "dataset",
          apiFilter: 'filter=dataset',
          apiQueryParams: '',
          textFieldHelpLink: `https://dataeuropa.gitlab.io/data-provider-manual/how-to-search/search-for-datasets/`,
          textKeys: {
            mobileSearchButton: 'message.datasets.ecd.filters.search',
            textFieldPlaceholder: 'message.datasets.searchBar.placeholder',
            count: "message.datasets.countMessage",
            loading: "message.datasets.loadingMessage"
          },
          getQuery: ["datasets", "getQuery"],
          setQuery: ["datasets", "setQuery"],
          setSort: ["datasets", "setSort"],
          getCount: ["datasets", "getDatasetsCount"],
          getLoading: ["datasets", "getLoading"],
          getFacets: ["datasets", "getFacets"],
        }
      }
      else return {
        scope: "combined",
        apiFilter: 'filters=catalogue,dataset,resource',
        apiQueryParams: 'resource=editorial-content',
        textFieldHelpLink: `https://dataeuropa.gitlab.io/data-provider-manual/how-to-search/search-for-datasets/`,
        textKeys: {
          mobileSearchButton: 'message.datasets.ecd.filters.search',
          textFieldPlaceholder: 'message.search.searchBarPlaceholder.combined',
          count: "message.search.countMessage.combined",
          loading: "Loading resources..."
        },
        getQuery: ["datasets", "getQuery"],
        setQuery: ["datasets", "setQuery"],
        setSort: ["datasets", "setSort"],
        getCount: ["search", "getTotalCount"],
        getLoading: ["datasets", "getLoading"],
        getFacets: ["datasets", "getFacets"],
      }
    },
    ...mapGetters("datasets", ["getFacets", "getQuery", "getAllAvailableFacets",
      "getLoading", "getDatasetsCount",]),
    normalizedAvailableFacets() {
      const locale = this.$route.query.locale || this.$i18n.locale || "en";
      const activeDataScopeFacetId = this.$env?.content?.datasets?.facets?.dataScopeFacetId || "dataScope";
      const facets = Array.isArray(this.getAllAvailableFacets)
        ? this.getAllAvailableFacets
        : [];

      const byId = new Map();
      facets.forEach((facet) => {
        if (!facet || !facet.id) return;
        const normalizedId = (facet.id === "dataScope" || facet.id === "corporate-body-classification")
          ? activeDataScopeFacetId
          : facet.id;
        const items = (Array.isArray(facet.items) ? facet.items : []).map((item) => {
          const rawTitle = item?.title;
          const titleMap =
            rawTitle && typeof rawTitle === "object" && rawTitle.title && typeof rawTitle.title === "object"
              ? rawTitle.title
              : rawTitle;
          const normalizedTitle =
            titleMap && typeof titleMap === "object"
              ? titleMap[locale] || titleMap.en || Object.values(titleMap).find((v) => typeof v === "string") || ""
              : rawTitle;
          return {
            ...item,
            title: normalizedTitle,
          };
        });

        const existing = byId.get(normalizedId);
        if (!existing) {
          byId.set(normalizedId, {
            ...facet,
            id: normalizedId,
            items,
          });
          return;
        }

        const existingItems = Array.isArray(existing.items) ? existing.items : [];
        const merged = new Map(existingItems.map((item) => [String(item.id), item]));
        items.forEach((item) => {
          if (!merged.has(String(item.id))) merged.set(String(item.id), item);
        });
        existing.items = Array.from(merged.values());
      });

      return Array.from(byId.values());
    },
    ...mapGetters("search", ["getShowFilter"]),
    showSaveSearch() {
      if(this.isDatasetseries) {
        return false;
      }else return true
      // return this.$route.query.showSaveSearch !== undefined;
    },
     contentClasses() {
      // Full width when facets are hidden or not in use
      if (!this.getShowFilter) {
         return 'col-12 px-lg-0 px-sm-2'; 
      }
      if (this.isDatasetseries) {
        return 'col-12 px-lg-0 px-sm-2';
      }
      // Default classes when facets are shown
      return 'col-md-9 col-12';
    }
  },
  methods: {
    resolveSelectedFacetLabel(facet, facetId, findFacetTitle) {
      if (facet?.field === "country" && String(facetId).toLowerCase() === "eu") {
        return "Europe";
      }

      const label = findFacetTitle(facet.field, facetId);
      if (typeof label === "string" && label.trim().length > 0) {
        return label;
      }

      const fallbackFacet = this.normalizedAvailableFacets.find(
        (availableFacet) => availableFacet.id === facet?.field
      );
      const fallbackItem = fallbackFacet?.items?.find(
        (item) => String(item.id) === String(facetId)
      );

      if (
        fallbackItem?.title &&
        String(fallbackItem.title).trim().length > 0
      ) {
        return fallbackItem.title;
      }

      return String(facetId);
    },
  }
}
</script>
